import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'

// Generate a wobbly, hand-drawn circle path
const generateWobblyCirclePath = (cx, cy, rx, ry, seed = 1) => {
  const points = []
  const segments = 32
  const wobble = Math.min(rx, ry) * 0.14

  const random = (i) => {
    const x = Math.sin(seed * 1000 + i * 127.1) * 43758.5453
    return x - Math.floor(x)
  }

  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2 - Math.PI / 2
    const wobbleX = (random(i * 2) - 0.5) * wobble
    const wobbleY = (random(i * 2 + 1) - 0.5) * wobble
    const x = cx + (rx + wobbleX) * Math.cos(angle)
    const y = cy + (ry + wobbleY) * Math.sin(angle)
    points.push({ x, y })
  }

  const lastPoint = points[points.length - 1]
  lastPoint.x += (random(99) - 0.5) * wobble * 0.5
  lastPoint.y += (random(100) - 0.5) * wobble * 0.5

  let d = `M ${points[0].x} ${points[0].y}`
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const curr = points[i]
    const cpX = (prev.x + curr.x) / 2 + (random(i * 3) - 0.5) * wobble * 0.5
    const cpY = (prev.y + curr.y) / 2 + (random(i * 3 + 1) - 0.5) * wobble * 0.5
    d += ` Q ${cpX} ${cpY} ${curr.x} ${curr.y}`
  }

  return d
}

// Animated crayon circle - hand-drawn wobbly stroke
const CrayonCircle = ({ cx, cy, rx, ry, color = '#e74c3c', delay = 0, duration = 800 }) => {
  const pathRef = useRef(null)
  const [pathLength, setPathLength] = useState(0)
  const [ready, setReady] = useState(false)
  const [animate, setAnimate] = useState(false)
  const path = useMemo(() => generateWobblyCirclePath(cx, cy, rx, ry, cx + cy), [cx, cy, rx, ry])

  useEffect(() => {
    if (pathRef.current) {
      const length = pathRef.current.getTotalLength()
      setPathLength(length)
      setReady(true)
    }
  }, [path])

  useEffect(() => {
    if (!ready) return
    const timer = setTimeout(() => setAnimate(true), delay)
    return () => clearTimeout(timer)
  }, [ready, delay])

  if (!ready) {
    return (
      <path
        ref={pathRef}
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={6}
      />
    )
  }

  return (
    <path
      ref={pathRef}
      d={path}
      fill="none"
      stroke={color}
      strokeWidth={6}
      strokeLinecap="round"
      style={{
        strokeDasharray: pathLength,
        strokeDashoffset: animate ? 0 : pathLength,
        transition: animate ? `stroke-dashoffset ${duration}ms ease-out` : 'none',
        opacity: 0.85,
      }}
    />
  )
}

// Animated scribble stroke - single animated path for background fill
const AnimatedScribbleStroke = ({ d, delay = 0, duration = 150, color = 'var(--uchu-yin)' }) => {
  const pathRef = useRef(null)
  const [pathLength, setPathLength] = useState(0)
  const [ready, setReady] = useState(false)
  const [animate, setAnimate] = useState(false)

  useEffect(() => {
    if (pathRef.current) {
      const length = pathRef.current.getTotalLength()
      setPathLength(length)
      setReady(true)
    }
  }, [d])

  useEffect(() => {
    if (!ready) return
    const timer = setTimeout(() => setAnimate(true), delay)
    return () => clearTimeout(timer)
  }, [ready, delay])

  if (!ready) {
    return (
      <path
        ref={pathRef}
        d={d}
        fill="none"
        stroke="transparent"
        strokeWidth={24}
      />
    )
  }

  return (
    <path
      ref={pathRef}
      d={d}
      fill="none"
      stroke={color}
      strokeWidth={18}
      strokeLinecap="round"
      style={{
        strokeDasharray: pathLength,
        strokeDashoffset: animate ? 0 : pathLength,
        transition: animate ? `stroke-dashoffset ${duration}ms ease-out` : 'none',
        opacity: 0.95,
      }}
    />
  )
}

// Generate scribbled strokes to fill a rectangular area like hand-coloring
const generateScribbleStrokes = (width, height, seed = 1) => {
  const random = (i) => {
    const x = Math.sin(seed * 1000 + i * 127.1) * 43758.5453
    return x - Math.floor(x)
  }

  const strokes = []
  const padding = 25
  const w = width + padding * 2
  const h = height + padding * 2
  const strokeSpacing = 3
  const wobble = 10
  const edgeExtension = 0

  let strokeIndex = 0
  for (let y = padding; y < h - padding; y += strokeSpacing) {
    const yOffset = (random(strokeIndex * 10) - 0.5) * 4
    const startX = padding - edgeExtension + (random(strokeIndex * 10 + 1) - 0.5) * wobble
    const endX = w - padding + edgeExtension + (random(strokeIndex * 10 + 2) - 0.5) * wobble

    const points = []
    const segments = 4
    for (let i = 0; i <= segments; i++) {
      const t = i / segments
      const x = startX + (endX - startX) * t
      const yWobble = (random(strokeIndex * 100 + i * 7) - 0.5) * wobble
      points.push({ x, y: y + yOffset + yWobble })
    }

    let d = `M ${points[0].x} ${points[0].y}`
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1]
      const curr = points[i]
      const cpX = (prev.x + curr.x) / 2 + (random(strokeIndex * 50 + i) - 0.5) * 3
      const cpY = (prev.y + curr.y) / 2 + (random(strokeIndex * 50 + i + 1) - 0.5) * 3
      d += ` Q ${cpX} ${cpY} ${curr.x} ${curr.y}`
    }

    strokes.push(d)
    strokeIndex++
  }

  for (let i = 0; i < 3; i++) {
    const startX = padding - edgeExtension + random(500 + i) * (w - padding * 2) * 0.3
    const startY = padding + (random(501 + i) - 0.5) * wobble
    const endX = w - padding + edgeExtension - random(502 + i) * (w - padding * 2) * 0.3
    const endY = h - padding + (random(503 + i) - 0.5) * wobble

    const midX = (startX + endX) / 2 + (random(504 + i) - 0.5) * wobble * 2
    const midY = (startY + endY) / 2 + (random(505 + i) - 0.5) * wobble * 2

    strokes.push(`M ${startX} ${startY} Q ${midX} ${midY} ${endX} ${endY}`)
  }

  return { strokes, width: w, height: h }
}

// Tutorial prompt with scribbled/hand-colored background
const TutorialPrompt = ({ children, position = 'bottom', delay = 0, style = {}, onClick, stepKey }) => {
  const [showContent, setShowContent] = useState(false)
  const [dimensions, setDimensions] = useState({ width: 320, height: 120 })
  const [dimensionsReady, setDimensionsReady] = useState(false)
  const [currentStepKey, setCurrentStepKey] = useState(stepKey)
  const contentRef = useRef(null)

  if (stepKey !== currentStepKey) {
    setCurrentStepKey(stepKey)
    setShowContent(false)
    setDimensionsReady(false)
  }

  // Use useLayoutEffect to measure dimensions synchronously before paint
  // This prevents the flash of wrong-sized content
  useLayoutEffect(() => {
    if (contentRef.current) {
      const rect = contentRef.current.getBoundingClientRect()
      const cardWidth = Math.min(window.innerWidth * 0.9, 400)
      setDimensions({ width: cardWidth, height: rect.height })
      setDimensionsReady(true)
    }
  }, [children])

  const positionStyles = {
    top: { top: '20px', left: '50%', transform: 'translateX(-50%)' },
    center: { top: '310px', left: '50%', transform: 'translate(-50%, -50%)' },
    centerBottom: { top: '325px', left: '50%', transform: 'translateX(-50%)' },
    bottom: { top: '470px', left: '50%', transform: 'translateX(-50%)' },
  }

  const scribble = useMemo(() =>
    generateScribbleStrokes(dimensions.width, dimensions.height, (stepKey || 0) * 1000 + dimensions.width + dimensions.height),
    [dimensions.width, dimensions.height, stepKey]
  )

  const strokeDelay = 10
  const strokeDuration = 120
  const scribbleCompleteTime = delay + (scribble.strokes.length * strokeDelay) + strokeDuration

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), scribbleCompleteTime)
    return () => clearTimeout(timer)
  }, [scribbleCompleteTime, currentStepKey])

  return (
    <div
      onClick={onClick}
      style={{
        position: 'absolute',
        ...positionStyles[position],
        zIndex: 10,
        pointerEvents: 'auto',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {dimensionsReady && (
        <svg
          key={`scribble-${stepKey}`}
          width={scribble.width}
          height={scribble.height}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: -1,
          }}
        >
          {scribble.strokes.map((d, i) => (
            <AnimatedScribbleStroke
              key={`${stepKey}-${i}`}
              d={d}
              delay={delay + i * strokeDelay}
              duration={strokeDuration}
              color="var(--uchu-yin)"
            />
          ))}
        </svg>
      )}
      <div
        ref={contentRef}
        style={{
          position: 'relative',
          padding: '12px 24px',
          width: 'min(calc(90vw - 56px), 344px)',
          textAlign: 'center',
          fontFamily: 'Itim, sans-serif',
          fontSize: '22px',
          lineHeight: '1.5',
          color: 'var(--uchu-yang)',
          opacity: showContent ? 1 : 0,
          visibility: showContent ? 'visible' : 'hidden',
          transition: 'opacity 300ms ease-out',
        }}
      >
        {children}
      </div>
    </div>
  )
}

// Helper to get element center position
const getElementCenter = (selector) => {
  const el = document.querySelector(selector)
  if (!el) return null
  const rect = el.getBoundingClientRect()
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
    width: rect.width,
    height: rect.height,
  }
}

// Skip button to exit tutorial - matches help button position and style
const SkipButton = ({ onClick, delay = 0 }) => {
  const [showContent, setShowContent] = useState(false)
  const size = 44
  const scribble = useMemo(() => generateScribbleStrokes(size, size, 9992), [])

  const strokeDelay = 10
  const strokeDuration = 120
  const scribbleCompleteTime = delay + (scribble.strokes.length * strokeDelay) + strokeDuration

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), scribbleCompleteTime)
    return () => clearTimeout(timer)
  }, [scribbleCompleteTime])

  return (
    <div
      style={{
        position: 'absolute',
        top: '0.75em',
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          width: '90%',
          maxWidth: '25em',
          display: 'flex',
          justifyContent: 'flex-start',
        }}
      >
        <div
          onClick={onClick}
          style={{
            width: size,
            height: size,
            zIndex: 10,
            pointerEvents: 'auto',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            marginLeft: '-0.3em',
          }}
        >
          <svg
            width={scribble.width}
            height={scribble.height}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            {scribble.strokes.map((d, i) => (
              <AnimatedScribbleStroke
                key={`skip-${i}`}
                d={d}
                delay={delay + i * strokeDelay}
                duration={strokeDuration}
                color="var(--uchu-yin)"
              />
            ))}
          </svg>
          <svg
            width="1.3em"
            height="1.3em"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--uchu-yang)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              position: 'relative',
              zIndex: 1,
              opacity: showContent ? 1 : 0,
              transition: 'opacity 300ms ease-out',
            }}
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </div>
      </div>
    </div>
  )
}

// Main Tutorial Overlay component
const TutorialOverlay = ({
  onComplete,
  currentWord,
  startWord,
  targetWord,
  cursorPosition,
  currentPath,
  optimalPath,
  currentLevel,
  isGameFinished,
  score,
  hintIndex,
  hintExpanded,
  bumpExpanded,
  onChangeLevel,
  modalWords,
  startStep = 0,
}) => {
  const [step, setStep] = useState(startStep)
  const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight })
  const [elementPositions, setElementPositions] = useState({})
  const prevPathLength = useRef(0)
  const prevHintIndex = useRef(-1)
  const prevLevel = useRef(1)
  const prevIsGameFinished = useRef(false)

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight })
    }
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  // Query element positions periodically
  const updateElementPositions = useCallback(() => {
    const positions = {}

    const letterElements = document.querySelectorAll('[data-letter-index]')
    letterElements.forEach((el) => {
      const idx = el.getAttribute('data-letter-index')
      positions[`letter-${idx}`] = getElementCenter(`[data-letter-index="${idx}"]`)
    })

    const keyElements = document.querySelectorAll('[data-key]')
    keyElements.forEach((el) => {
      const key = el.getAttribute('data-key')
      positions[`key-${key}`] = getElementCenter(`[data-key="${key}"]`)
    })

    positions.startWord = getElementCenter('[data-start-word]')
    positions.targetWord = getElementCenter('[data-target-word]')

    const pathWordElements = document.querySelectorAll('[data-path-word]')
    pathWordElements.forEach((el) => {
      const idx = el.getAttribute('data-path-word')
      positions[`pathWord-${idx}`] = getElementCenter(`[data-path-word="${idx}"]`)
    })

    positions.hintButton = getElementCenter('[data-hint-button]')
    positions.boostButton = getElementCenter('[data-bump-button]')
    positions.confirmHint = getElementCenter('[data-confirm-hint]')
    positions.confirmBoost = getElementCenter('[data-confirm-boost]')
    positions.scoreDisplay = getElementCenter('[data-score-display]')
    positions.bestCircle = getElementCenter('[data-best-circle]')

    // Level tabs
    positions.levelTab1 = getElementCenter('[data-level-tab="1"]')
    positions.levelTab2 = getElementCenter('[data-level-tab="2"]')

    // Best path words (from modalWords when best circle is expanded)
    const bestPathElements = document.querySelectorAll('[data-best-path-word]')
    bestPathElements.forEach((el) => {
      const idx = el.getAttribute('data-best-path-word')
      positions[`bestPathWord-${idx}`] = getElementCenter(`[data-best-path-word="${idx}"]`)
    })

    setElementPositions(positions)
  }, [])

  useEffect(() => {
    updateElementPositions()
    const interval = setInterval(updateElementPositions, 300)
    return () => clearInterval(interval)
  }, [updateElementPositions])

  // Step transitions based on user actions

  // Step 2 -> 3: Player taps a letter
  useEffect(() => {
    if (step === 2 && cursorPosition === 2) {
      setStep(3)
    }
  }, [step, cursorPosition])

  // Step 3 -> 4: Player makes first word (path grows)
  useEffect(() => {
    if (step === 3 && currentPath.length > prevPathLength.current) {
      setStep(4)
    }
    if (step === 3) {
      prevPathLength.current = currentPath.length
    }
  }, [step, currentPath.length])

  // Step 4 -> 5: Player undoes (path shrinks)
  useEffect(() => {
    if (step === 4 && currentPath.length < prevPathLength.current) {
      setStep(5)
    }
    if (step === 4) {
      prevPathLength.current = currentPath.length
    }
  }, [step, currentPath.length])

  // Step 5 -> 6: Player finishes level 1
  useEffect(() => {
    if (step === 5 && isGameFinished && !prevIsGameFinished.current) {
      setStep(6)
    }
    prevIsGameFinished.current = isGameFinished
  }, [step, isGameFinished])

  // Step 6 -> 7: After level complete, wait for level 2 to become active
  useEffect(() => {
    if (step === 6 && currentLevel === 2 && prevLevel.current === 1) {
      setStep(7)
    }
    prevLevel.current = currentLevel
  }, [step, currentLevel])

  // Step 8 -> 9: Player opens LETTER hint
  useEffect(() => {
    if (step === 8 && hintExpanded) {
      setStep(9)
    }
  }, [step, hintExpanded])

  // Step 9 -> 10: Player confirms hint (hintIndex changes from -1)
  useEffect(() => {
    if (step === 9 && hintIndex >= 0 && prevHintIndex.current === -1) {
      setStep(10)
    }
    prevHintIndex.current = hintIndex
  }, [step, hintIndex])

  // Step 11 -> 12: Player opens WORD boost
  useEffect(() => {
    if (step === 11 && bumpExpanded) {
      setStep(12)
    }
  }, [step, bumpExpanded])

  // Step 12 -> 13: Player confirms boost (path length increases)
  useEffect(() => {
    if (step === 12 && currentPath.length > prevPathLength.current) {
      setStep(13)
    }
    if (step === 12) {
      prevPathLength.current = currentPath.length
    }
  }, [step, currentPath.length])

  const { width, height } = dimensions

  const renderStep = () => {
    const startPos = elementPositions.startWord
    const targetPos = elementPositions.targetWord

    switch (step) {
      case 0: {
        return (
          <>
            <TutorialPrompt key="prompt-0" position="centerBottom" delay={200} stepKey={0}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                Welcome to <span style={{ fontFamily: 'Title', fontSize: '1.1em' }}>subletters</span>!
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '16px', marginBottom: '8px' }}>
                <button
                  style={{
                    padding: '8px 16px',
                    fontSize: '1em',
                    fontWeight: '500',
                    color: 'var(--uchu-yin)',
                    backgroundColor: 'var(--uchu-light-gray)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => setStep(1)}
                >
                  Tutorial
                </button>
                <button
                  style={{
                    padding: '8px 16px',
                    fontSize: '1em',
                    fontWeight: '500',
                    color: 'var(--uchu-yin)',
                    backgroundColor: 'var(--uchu-light-gray)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => onComplete()}
                >
                  Play Now
                </button>
              </div>
            </TutorialPrompt>
          </>
        )
      }

      case 1: {
        return (
          <>
            <svg key="svg-step-1" width={width} height={height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              {startPos && (
                <CrayonCircle
                  key="circle-start"
                  cx={startPos.x}
                  cy={startPos.y}
                  rx={startPos.width / 2 + 12}
                  ry={startPos.height / 2 + 8}
                  color="#3498db"
                  delay={300}
                  duration={600}
                />
              )}
              {targetPos && (
                <CrayonCircle
                  key="circle-target"
                  cx={targetPos.x}
                  cy={targetPos.y}
                  rx={targetPos.width / 2 + 12}
                  ry={targetPos.height / 2 + 8}
                  color="#27ae60"
                  delay={800}
                  duration={600}
                />
              )}
            </svg>
            <TutorialPrompt key="prompt-1" position="centerBottom" delay={200} stepKey={1} onClick={() => setStep(2)}>
              <div style={{ marginBottom: '12px' }}>
                Turn the <span style={{ color: '#3498db', fontWeight: '600' }}>start word</span> into the{' '}
                <span style={{ color: '#27ae60', fontWeight: '600' }}>target word</span>, one letter at a time.
              </div>
              <div className="tap-to-continue" style={{ fontSize: '1.1em', color: 'var(--uchu-yang)', fontWeight: '500' }}>- continue -</div>
            </TutorialPrompt>
          </>
        )
      }

      case 2: {
        const letterPos = elementPositions['letter-2']
        return (
          <>
            <svg key="svg-step-2" width={width} height={height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              {letterPos && (
                <CrayonCircle
                  key="circle-letter"
                  cx={letterPos.x}
                  cy={letterPos.y}
                  rx={letterPos.width / 2 + 10}
                  ry={letterPos.height / 2 + 10}
                  color="#e74c3c"
                  delay={300}
                />
              )}
            </svg>
            <TutorialPrompt key="prompt-2" position="centerBottom" delay={200} stepKey={2}>
              <div>Tap a <span style={{ color: '#e74c3c', fontWeight: '600' }}>letter</span> you want to change.</div>
            </TutorialPrompt>
          </>
        )
      }

      case 3: {
        return (
          <TutorialPrompt key="prompt-3" position="bottom" delay={200} stepKey={3}>
            <div>Tap a <span style={{ color: '#27ae60', fontWeight: '600' }}>replacement letter</span> on the keyboard to form a new word.</div>
          </TutorialPrompt>
        )
      }

      case 4: {
        // Undo teaching: circle first path word
        const pathWordPos = elementPositions['pathWord-0']
        return (
          <>
            <svg key="svg-step-4" width={width} height={height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              {pathWordPos && (
                <CrayonCircle
                  key="circle-pathword-0"
                  cx={pathWordPos.x}
                  cy={pathWordPos.y}
                  rx={pathWordPos.width / 2 + 10}
                  ry={pathWordPos.height / 2 + 6}
                  color="#e74c3c"
                  delay={300}
                />
              )}
            </svg>
            <TutorialPrompt key="prompt-4" position="centerBottom" delay={200} stepKey={4}>
              <div>Tap any word in your path to <span style={{ color: '#e74c3c', fontWeight: '600' }}>undo back to that word</span>.</div>
            </TutorialPrompt>
          </>
        )
      }

      case 5: {
        return (
          <TutorialPrompt key="prompt-5" position="bottom" delay={200} stepKey={5}>
            <div>Now reach the target word!</div>
          </TutorialPrompt>
        )
      }

      case 6: {
        // Waiting for level 2 to become active - brief transition
        return null
      }

      case 7: {
        // Score explanation (triggered when level 2 becomes active)
        const scorePos = elementPositions.scoreDisplay
        return (
          <>
            <svg key="svg-step-7" width={width} height={height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              {scorePos && (
                <CrayonCircle
                  key="circle-score"
                  cx={scorePos.x}
                  cy={scorePos.y}
                  rx={scorePos.width / 2 + 12}
                  ry={scorePos.height / 2 + 10}
                  color="#f39c12"
                  delay={300}
                />
              )}
            </svg>
            <TutorialPrompt key="prompt-7" position="centerBottom" delay={200} stepKey={7} onClick={() => setStep(8)}>
              <div style={{ marginBottom: '12px' }}>
                This is your <strong style={{ color: '#f39c12' }}>score</strong>. You get points for completing a level. You get more for finding an optimally short path.
              </div>
              <div className="tap-to-continue" style={{ fontSize: '1.1em', color: 'var(--uchu-yang)', fontWeight: '500' }}>- continue -</div>
            </TutorialPrompt>
          </>
        )
      }

      case 8: {
        // LETTER hint teaching
        const hintPos = elementPositions.hintButton
        return (
          <>
            <svg key="svg-step-8" width={width} height={height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              {hintPos && (
                <CrayonCircle
                  key="circle-hint"
                  cx={hintPos.x}
                  cy={hintPos.y}
                  rx={hintPos.width / 2 + 8}
                  ry={hintPos.height / 2 + 6}
                  color="#3498db"
                  delay={300}
                />
              )}
            </svg>
            <TutorialPrompt key="prompt-8" position="centerBottom" delay={200} stepKey={8}>
              <div>If you need a hint, tap <span style={{ color: '#3498db', fontWeight: '600' }}>LETTER</span> to reveal which letter to change to stay on an optimal path.</div>
            </TutorialPrompt>
          </>
        )
      }

      case 9: {
        // Confirm hint
        const confirmPos = elementPositions.confirmHint
        return (
          <>
            <svg key="svg-step-9" width={width} height={height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              {confirmPos && (
                <CrayonCircle
                  key="circle-confirm-hint"
                  cx={confirmPos.x}
                  cy={confirmPos.y}
                  rx={confirmPos.width / 2 + 8}
                  ry={confirmPos.height / 2 + 8}
                  color="#27ae60"
                  delay={500}
                />
              )}
            </svg>
            <TutorialPrompt key="prompt-9" position="centerBottom" delay={200} stepKey={9}>
              <div>Revealing a letter costs 30 points. Tap <span style={{ color: '#27ae60', fontWeight: '600' }}>confirm</span> below.</div>
            </TutorialPrompt>
          </>
        )
      }

      case 10: {
        // Hint result
        return (
          <>
            <TutorialPrompt key="prompt-10" position="centerBottom" delay={200} stepKey={10} onClick={() => setStep(11)}>
              <div style={{ marginBottom: '12px' }}>
                Notice how the letter you need to change is now <span style={{ color: '#3498db', fontWeight: '600' }}>highlighted</span>.
              </div>
              <div className="tap-to-continue" style={{ fontSize: '1.1em', color: 'var(--uchu-yang)', fontWeight: '500' }}>- continue -</div>
            </TutorialPrompt>
          </>
        )
      }

      case 11: {
        // WORD boost teaching
        const boostPos = elementPositions.boostButton
        return (
          <>
            <svg key="svg-step-11" width={width} height={height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              {boostPos && (
                <CrayonCircle
                  key="circle-boost"
                  cx={boostPos.x}
                  cy={boostPos.y}
                  rx={boostPos.width / 2 + 8}
                  ry={boostPos.height / 2 + 6}
                  color="#9b59b6"
                  delay={300}
                />
              )}
            </svg>
            <TutorialPrompt key="prompt-11" position="centerBottom" delay={200} stepKey={11}>
              <div>If you get stuck, tap <span style={{ color: '#9b59b6', fontWeight: '600' }}>WORD</span> to reveal the next word on an optimal path.</div>
            </TutorialPrompt>
          </>
        )
      }

      case 12: {
        // Confirm boost
        const confirmPos = elementPositions.confirmBoost
        return (
          <>
            <svg key="svg-step-12" width={width} height={height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              {confirmPos && (
                <CrayonCircle
                  key="circle-confirm-boost"
                  cx={confirmPos.x}
                  cy={confirmPos.y}
                  rx={confirmPos.width / 2 + 8}
                  ry={confirmPos.height / 2 + 8}
                  color="#27ae60"
                  delay={500}
                />
              )}
            </svg>
            <TutorialPrompt key="prompt-12" position="centerBottom" delay={200} stepKey={12}>
              <div>Revealing a word costs 75 points. You only have four of these per day. Tap <span style={{ color: '#27ae60', fontWeight: '600' }}>confirm</span> below.</div>
            </TutorialPrompt>
          </>
        )
      }

      case 13: {
        return (
          <TutorialPrompt key="prompt-13" position="centerBottom" delay={200} stepKey={13} onClick={() => setStep(14)}>
            <div style={{ marginBottom: '12px' }}>
              Both <span style={{ color: 'var(--uchu-blue)', fontWeight: '600' }}>LETTER</span>s and{' '}
              <span style={{ color: 'var(--uchu-purple)', fontWeight: '600' }}>WORD</span>s will place you back onto an optimal path if you diverged from one.
            </div>
            <div className="tap-to-continue" style={{ fontSize: '1.1em', color: 'var(--uchu-yang)', fontWeight: '500' }}>- continue -</div>
          </TutorialPrompt>
        )
      }

      case 14: {
        return (
          <TutorialPrompt key="prompt-14" position="centerBottom" delay={200} stepKey={14} onClick={() => setStep(15)}>
            <div style={{ marginBottom: '12px' }}>
              Some letters may need to be changed more than once, and some not at all.
            </div>
            <div className="tap-to-continue" style={{ fontSize: '1.1em', color: 'var(--uchu-yang)', fontWeight: '500' }}>- continue -</div>
          </TutorialPrompt>
        )
      }

      case 15: {
        // Final step
        return (
          <TutorialPrompt key="prompt-15" position="centerBottom" delay={200} stepKey={15} onClick={onComplete}>
            <div style={{ fontSize: '22px', fontWeight: '600', marginBottom: '8px' }}>
              That's it! New puzzles appear daily.
            </div>
            <div style={{ marginBottom: '12px' }}>
              Ready for today's puzzles?
            </div>
            <div className="tap-to-continue" style={{ fontSize: '1.1em', color: 'var(--uchu-yang)', fontWeight: '500' }}>- play -</div>
          </TutorialPrompt>
        )
      }

      default:
        return null
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'transparent',
        zIndex: 100,
        pointerEvents: 'none',
      }}
    >
      {step >= 1 && <SkipButton onClick={onComplete} delay={200} />}
      {renderStep()}
    </div>
  )
}

export default TutorialOverlay
